/*
 * W11: the languages the product ships (spec W10). Adding one is a catalogue file and a line here — no code elsewhere.
 * `en-XA` is the pseudo-locale for layout checks (accented, 35 % longer, bracketed): never offered to users.
 */
export const LOCALES = ['en', 'tr', 'de', 'fr', 'es', 'it', 'nl', 'pl', 'pt', 'ru', 'uk', 'ar', 'zh-Hans', 'ja'] as const;
export type ShippedLocale = (typeof LOCALES)[number];
export const PSEUDO_LOCALE = 'en-XA' as const;
export type Locale = ShippedLocale | typeof PSEUDO_LOCALE;

export interface LocaleMeta {
  /** The language's own name, as its speakers write it. */
  nativeName: string;
  /** BCP-47 tag for `Intl`. */
  intl: string;
  dir: 'ltr' | 'rtl';
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { nativeName: 'English', intl: 'en-US', dir: 'ltr' },
  tr: { nativeName: 'Türkçe', intl: 'tr-TR', dir: 'ltr' },
  de: { nativeName: 'Deutsch', intl: 'de-DE', dir: 'ltr' },
  fr: { nativeName: 'Français', intl: 'fr-FR', dir: 'ltr' },
  es: { nativeName: 'Español', intl: 'es-ES', dir: 'ltr' },
  it: { nativeName: 'Italiano', intl: 'it-IT', dir: 'ltr' },
  nl: { nativeName: 'Nederlands', intl: 'nl-NL', dir: 'ltr' },
  pl: { nativeName: 'Polski', intl: 'pl-PL', dir: 'ltr' },
  pt: { nativeName: 'Português', intl: 'pt-PT', dir: 'ltr' },
  ru: { nativeName: 'Русский', intl: 'ru-RU', dir: 'ltr' },
  uk: { nativeName: 'Українська', intl: 'uk-UA', dir: 'ltr' },
  ar: { nativeName: 'العربية', intl: 'ar', dir: 'rtl' },
  'zh-Hans': { nativeName: '简体中文', intl: 'zh-Hans-CN', dir: 'ltr' },
  ja: { nativeName: '日本語', intl: 'ja-JP', dir: 'ltr' },
  'en-XA': { nativeName: 'Pseudo (en-XA)', intl: 'en-US', dir: 'ltr' },
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (value === PSEUDO_LOCALE || (LOCALES as readonly string[]).includes(value));
}

/** A browser or account language tag (`pt-BR`, `zh-CN`, `de`) → the shipped locale that serves it, if any. */
export function matchLocale(tag: string | null | undefined): ShippedLocale | null {
  if (!tag) return null;
  const t = tag.toLowerCase();
  if (t.startsWith('zh')) return 'zh-Hans';
  const base = t.split('-')[0] ?? '';
  return (LOCALES as readonly string[]).includes(base) ? (base as ShippedLocale) : null;
}

const ACCENTS: Record<string, string> = { a: 'á', e: 'é', i: 'í', o: 'ö', u: 'ü', c: 'ç', n: 'ñ', s: 'š', y: 'ý', A: 'Á', E: 'É', I: 'Í', O: 'Ö', U: 'Ü', C: 'Ç', N: 'Ñ', S: 'Š', Y: 'Ý' };

/** The pseudo-locale rendering of an English pattern: letters accented outside `{…}` arguments, 35 % longer, bracketed. */
export function pseudoize(pattern: string): string {
  let depth = 0;
  let out = '';
  for (const ch of pattern) {
    if (ch === '{') depth += 1;
    if (ch === '}') depth = Math.max(0, depth - 1);
    out += depth === 0 ? (ACCENTS[ch] ?? ch) : ch;
  }
  const pad = '·'.repeat(Math.ceil(pattern.replace(/\{[^}]*\}/g, '').length * 0.35));
  return `⟦${out}${pad ? ` ${pad}` : ''}⟧`;
}
